import {
    createContext,
    useContext,
    createEffect,
} from 'solid-js'
import {
    useLocation,
    useNavigate,
    useSearchParams,
} from '@solidjs/router'
import {
    DEFAULT_LOCALE,
    getHostDefaultLocale,
    normalizeLocale,
} from '../data/static/locale-config.js'
import {
    buildPathnameForLocale,
    getLocaleFromPathname,
    getPathWithoutLocalePrefix,
    pathnameHasLocalePrefix,
    searchHashSuffix,
} from '../utils/locale-path.js'
import {
    readLocalePreference,
    writeLocalePreference,
} from '../../stores/locale-currency-preference-store.js'
import { localeSwitchNeedsReload } from '../utils/google-maps-locale.js'
import SessionService from '../services/session-service.js'
import ProfileService from '../services/profile-service.js'

const LocaleContext = createContext()

// TODO: Future improvement - detect browser's default language/locale on first visit
// and use that as the initial locale if supported

export function LocaleProvider (props) {
    const location = useLocation()
    const navigate = useNavigate()
    const [searchParameters] = useSearchParams()

    const locale = () => getLocaleFromPathname(location.pathname)

    // Always strips locale prefix to ensure canonical URLs (prevents both /en/test and /test from working)
    const pathWithoutLocale = () => getPathWithoutLocalePrefix(location.pathname)

    // Unprefixed URL only: send user to the right locale. Precedence:
    //   1. `?locale=` query param (e.g. from the OAuth /complete-profile redirect, reflecting the
    //      language the user picked on Core's hosted page) — a fresh, explicit signal that hasn't
    //      been written to localStorage yet, so it wins and is persisted.
    //   2. Saved preference (localStorage), including an explicit `en` which blocks the host default.
    //   3. Host default (e.g. lodgr.mx → es-mx) for first-time visitors with no saved preference.
    // The initial page load is also handled pre-mount in main.jsx (redirectToPreferredOrHostLocale);
    // this effect covers in-app SPA navigations to an unprefixed URL.
    createEffect(() => {
        const pathname = location.pathname
        const suffix = searchHashSuffix(location.search, location.hash)

        if (pathnameHasLocalePrefix(pathname)) {
            return
        }

        const queryLocale = normalizeLocale(searchParameters.locale)
        if (queryLocale) {
            writeLocalePreference(queryLocale)
        }

        const target = queryLocale ?? readLocalePreference() ?? getHostDefaultLocale(location.search)
        if (!target || target === DEFAULT_LOCALE) {
            return
        }

        const basePath = getPathWithoutLocalePrefix(pathname)
        const nextPath = buildPathnameForLocale(basePath, target)
        navigate(`${nextPath}${suffix}`, {
            replace: true,
        })
    })

    // Sync profile locale with path locale if user is authenticated
    let hasSyncedProfiledLocale = false
    createEffect (async () => {
        if (!SessionService.isAuthenticated()){
            hasSyncedProfiledLocale = false
            return
        }
        if (hasSyncedProfiledLocale){
            return
        }
        try {
            const profile = await ProfileService.show()
            const preferredLocale = profile?.language_preference
            if (preferredLocale && preferredLocale !== locale()){
                switchLocale(preferredLocale)
            }
            hasSyncedProfiledLocale = true
        }catch (error){
            console.warn ('LocaleContext: Failed to sync profile locale', error)
        }
    })

    // Switch locale (navigates to new URL)
    // Preserves search params and hash to avoid losing state when switching locales
    const switchLocale = (newLocale) => {
        writeLocalePreference(newLocale)
        const basePath = pathWithoutLocale()
        const suffix = searchHashSuffix(location.search, location.hash)
        const nextPath = buildPathnameForLocale(basePath, newLocale)
        // A map on screen keeps the language its script loaded with; only a full load changes it.
        if (localeSwitchNeedsReload(newLocale)) {
            window.location.assign(`${nextPath}${suffix}`)
            return
        }
        navigate(`${nextPath}${suffix}`)
    }

    /**
     * Add locale prefix to a path
     * @param {string} path - Path to add locale prefix to (e.g., '/account' or 'account')
     * @param {string} [targetLocale] - Optional locale to use (defaults to current locale)
     * @returns {string} Path with locale prefix (e.g., '/fr/account' or '/account')
     */
    const addLocalePrefix = (path, targetLocale = null) => {
        const loc = targetLocale || locale()
        const normalizedPath = path.startsWith('/') ? path : `/${path}`
        const basePath = getPathWithoutLocalePrefix(normalizedPath)
        return buildPathnameForLocale(basePath, loc)
    }

    /**
     * Locale-aware navigate function that preserves the current locale
     * @param {string} to - Path to navigate to (will have locale prefix added)
     * @param {object} options - Navigation options (same as useNavigate)
     */
    const navigateWithLocale = (to, options = {}) => {
        const pathWithLocale = addLocalePrefix(to)
        navigate(pathWithLocale, options)
    }

    return (
        <LocaleContext.Provider value={{
            addLocalePrefix,
            locale,
            navigateWithLocale,
            pathWithoutLocale,
            switchLocale,
        }}>
            {props.children}
        </LocaleContext.Provider>
    )
}

export const useLocale = () => useContext(LocaleContext)
